import React, { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { APP_NAME, NAVIGATION_LINKS } from '../constants';
import { NavLinkItem } from '../types';
import { MenuIcon, XIcon, AcademicCapIcon, CogIcon } from './icons';
import { useTheme } from '../contexts/ThemeContext';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { colors } = useTheme();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const linkClasses = "px-3 py-2 rounded-md text-sm font-medium transition-all duration-300";
  const activeLinkClasses = "shadow-lg";
  const inactiveLinkClasses = "hover:scale-105";
  
  const renderNavLink = (item: NavLinkItem, isMobile: boolean = false) => (
    <NavLink
      key={item.path}
      to={item.path}
      onClick={() => isMobile && setIsMobileMenuOpen(false)}
      className={({ isActive }) => 
        `${linkClasses} ${isActive ? activeLinkClasses : inactiveLinkClasses} ${isMobile ? 'block' : ''}`
      }
      style={({ isActive }) => ({
        backgroundColor: isActive ? `${colors.primary.main}15` : 'transparent',
        color: isActive ? colors.primary.main : colors.text.secondary,
        borderBottom: isActive ? `2px solid ${colors.accent.mint}` : '2px solid transparent'
      })}
      onMouseEnter={(e) => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.backgroundColor = `${colors.accent.mint}10`;
          e.currentTarget.style.color = colors.accent.mint;
        }
      }}
      onMouseLeave={(e) => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = colors.text.secondary;
        }
      }}
    >
      {item.label}
    </NavLink>
  );

  const adminLinks = [
    {
      to: '/admin/settings',
      label: 'Settings',
      icon: <CogIcon className="w-5 h-5" />,
    },
  ];

  return (
    <header 
      className="backdrop-blur-md shadow-lg sticky top-0 z-50"
      style={{ backgroundColor: `${colors.background.default}ee` }}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center space-x-2 text-2xl font-bold transition-all duration-300 hover:scale-105">
              <AcademicCapIcon className="h-8 w-8" style={{ color: colors.accent.mint }} />
              <span style={{ 
                background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.accent.mint} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>{APP_NAME}</span>
            </Link>
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-4">
              {NAVIGATION_LINKS.filter(item => item.label !== 'Admin Panel').map(item => renderNavLink(item))}
              <Link
                to="/admin"
                className={`${linkClasses} text-sm transition-all duration-300`}
                style={{
                  backgroundColor: 'transparent',
                  color: colors.text.secondary,
                  border: `1px solid ${colors.text.secondary}30`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${colors.primary.main}10`;
                  e.currentTarget.style.color = colors.primary.main;
                  e.currentTarget.style.borderColor = `${colors.primary.main}50`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = colors.text.secondary;
                  e.currentTarget.style.borderColor = `${colors.text.secondary}30`;
                }}
              >
                Admin
              </Link>
            </div>
          </div>
          <div className="md:hidden flex items-center">
            <button
              onClick={toggleMobileMenu}
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-neutral-light hover:text-white hover:bg-neutral-dark focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {isMobileMenuOpen ? (
                <XIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <MenuIcon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden" id="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {NAVIGATION_LINKS.filter(item => item.label !== 'Admin Panel').map(item => renderNavLink(item, true))}
            <Link
              to="/admin"
              onClick={() => setIsMobileMenuOpen(false)}
              className={`${linkClasses} text-sm transition-all duration-300 block`}
              style={{
                backgroundColor: 'transparent',
                color: colors.text.secondary,
                border: `1px solid ${colors.text.secondary}30`
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.primary.main}10`;
                e.currentTarget.style.color = colors.primary.main;
                e.currentTarget.style.borderColor = `${colors.primary.main}50`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = colors.text.secondary;
                e.currentTarget.style.borderColor = `${colors.text.secondary}30`;
              }}
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
    